import type React from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import {
  SignInCard, SignInWordmark, SignInTagline,
  Input, PrimaryButton, InlineError,
} from '../styled-components';
import { WaveformBackground } from '../components';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';

// ---------------------------------------------------------------------------
// Local styled components
// ---------------------------------------------------------------------------

const PageWrapper = styled.div({
  width: '100%',
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 16,
  background: 'var(--bg-base)',
  position: 'relative',
  overflow: 'auto',
  padding: '40px 16px',
  boxSizing: 'border-box',
  '@media (max-width: 480px)': {
    gap: 10,
    padding: '24px 12px',
  },
});

const Logo = styled.img({
  height: 400,
  filter: 'grayscale(1) brightness(0.75)',
  margin: '-80px 0',
  position: 'relative',
  zIndex: 1,
  '@media (max-width: 480px)': {
    height: 200,
    margin: '-40px 0',
  },
});

const Card = styled(SignInCard)({
  '@media (max-width: 480px)': {
    padding: '28px 20px',
    width: '100%',
    borderRadius: 12,
  },
});

const FormField = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
});

const Label = styled.label({
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  letterSpacing: '0.04em',
});

const OptionalTag = styled.span({
  fontWeight: 400,
  color: 'var(--text-muted)',
  marginLeft: 4,
});

const CheckboxRow = styled.label({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  cursor: 'pointer',
  fontSize: 13,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
  '@media (max-width: 480px)': {
    fontSize: 12,
    gap: 8,
  },
});

const Checkbox = styled.input({
  marginTop: 3,
  accentColor: 'var(--accent-cyan)',
  width: 16,
  height: 16,
  flexShrink: 0,
  cursor: 'pointer',
  '@media (max-width: 480px)': {
    width: 20,
    height: 20,
    marginTop: 1,
  },
});

const LegalLink = styled.a({
  color: 'var(--accent-cyan)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  '&:hover': {
    color: 'var(--text-primary)',
  },
});

const SuccessMessage = styled.div({
  textAlign: 'center',
  color: 'var(--accent-cyan)',
  fontSize: 14,
  lineHeight: 1.6,
  padding: '20px 0',
});

const CampaignDescription = styled.div({
  fontSize: 13,
  color: 'var(--text-secondary)',
  textAlign: 'center',
  lineHeight: 1.5,
  padding: '0 0 4px',
});

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

type FormState =
  | { step: 'idle' }
  | { step: 'submitting' }
  | { step: 'error'; message: string }
  | { step: 'submitted' };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OptInPage() {
  const [formState, setFormState] = useState<FormState>({ step: 'idle' });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consented, setConsented] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!consented) return;
    setFormState({ step: 'submitting' });
    try {
      await fetchOrThrow('/dashboard/api/opt-in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [CSRF_HEADER_NAME]: getCSRFToken(),
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
        }),
      });
      setFormState({ step: 'submitted' });
    } catch {
      setFormState({ step: 'error', message: 'Something went wrong. Please try again.' });
    }
  }

  return (
    <PageWrapper>
      <WaveformBackground />

      <SignInWordmark>
        <Logo src="/static/guava-logo.png" alt="Guava" />
      </SignInWordmark>
      <SignInTagline>Test Polling Survey</SignInTagline>

      <Card>
        {formState.step === 'submitted' ? (
          <SuccessMessage>
            You have been opted in to receive survey messages from Guava.
            <br />
            You can opt out at any time by texting STOP.
          </SuccessMessage>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <CampaignDescription>
              By opting in below, you agree to receive survey and polling text messages from Guava.
            </CampaignDescription>

            {formState.step === 'error' && (
              <InlineError>{formState.message}</InlineError>
            )}

            <FormField>
              <Label htmlFor="opt-in-name">Name</Label>
              <Input
                id="opt-in-name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </FormField>

            <FormField>
              <Label htmlFor="opt-in-email">Email</Label>
              <Input
                id="opt-in-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormField>

            <FormField>
              <Label htmlFor="opt-in-phone">
                Phone number <OptionalTag>(optional)</OptionalTag>
              </Label>
              <Input
                id="opt-in-phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </FormField>

            <CheckboxRow>
              <Checkbox
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
              />
              <span>
                By checking this box, you agree to receive survey and customer care
                text messages from Guava. Reply STOP to opt out. Message and data
                rates may apply. Message frequency varies.{' '}
                <LegalLink href="https://goguava.ai/terms" target="_blank" rel="noreferrer">Terms of Service</LegalLink>
                {' | '}
                <LegalLink href="https://goguava.ai/privacy" target="_blank" rel="noreferrer">Privacy Policy</LegalLink>
              </span>
            </CheckboxRow>

            <PrimaryButton
              type="submit"
              disabled={formState.step === 'submitting' || !consented || !name.trim() || !email.trim()}
              style={{
                fontFamily: "'Jost', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: 13,
              }}
            >
              {formState.step === 'submitting' ? 'Submitting...' : 'Opt In'}
            </PrimaryButton>
          </form>
        )}
      </Card>
    </PageWrapper>
  );
}
